import React from 'react';

const CallsTab = ({
  callsForm,
  setCallsForm,
  callHistory,
  availableCallVoices,
  voiceSearchTerm,
  setVoiceSearchTerm,
  showVoiceModal,
  setShowVoiceModal,
  loading,
  loadingCalls,
  globalTwilioNumber,
  attendees,
  handleSaveCalls,
  handleConfigureTwilioWebhook,
  handleConfigureSIPAuth,
  handleStartCallCampaign,
  handleRunDiagnostic,
}) => {
  return (
    <div className="card">
      <h2 className="text-xl font-bold mb-4">Llamadas Telefónicas (Retell AI + Twilio)</h2>
      <p className="text-gray-600 mb-6">
        Configura llamadas automáticas con IA para confirmar asistencia de tus invitados
      </p>

      <form onSubmit={handleSaveCalls} className="space-y-6">
        {/* Configuración del Agente de IA */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-semibold mb-4">Agente de IA</h3>

          <div className="space-y-4">
            <div>
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={callsForm.callEnabled}
                  onChange={(e) => setCallsForm({...callsForm, callEnabled: e.target.checked})}
                  className="mr-2 h-4 w-4"
                />
                <span className="font-medium">Habilitar llamadas telefónicas para este proyecto</span>
              </label>
            </div>

            <div>
              <label className="label">Nombre del Agente</label>
              <input
                type="text"
                value={callsForm.agentName}
                onChange={(e) => setCallsForm({...callsForm, agentName: e.target.value})}
                className="input-field"
                placeholder="Asistente Virtual - Evento 2026"
                disabled={!callsForm.callEnabled}
              />
            </div>

            <div>
              <label className="label">Voz del Agente</label>
              <div className="space-y-2">
                {/* Voice Search Input */}
                <div className="relative">
                  <input
                    type="text"
                    value={voiceSearchTerm}
                    onChange={(e) => setVoiceSearchTerm(e.target.value)}
                    onClick={() => setShowVoiceModal(true)}
                    placeholder="Buscar voz... (ej: Spanish, female, Matilda)"
                    className="input-field pr-10"
                    disabled={!callsForm.callEnabled}
                  />
                  <button
                    type="button"
                    onClick={() => setShowVoiceModal(true)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={!callsForm.callEnabled}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                </div>

                {/* Selected Voice Display */}
                {callsForm.agentVoice && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                    <div className="font-medium text-blue-900">
                      Voz seleccionada: {availableCallVoices.find(v => v.voice_id === callsForm.agentVoice)?.voice_name || callsForm.agentVoice}
                    </div>
                    {availableCallVoices.find(v => v.voice_id === callsForm.agentVoice) && (
                      <div className="text-blue-700 mt-1">
                        {availableCallVoices.find(v => v.voice_id === callsForm.agentVoice)?.language || 'N/A'} •
                        {availableCallVoices.find(v => v.voice_id === callsForm.agentVoice)?.gender || 'N/A'} •
                        {availableCallVoices.find(v => v.voice_id === callsForm.agentVoice)?.accent || 'N/A'}
                      </div>
                    )}
                  </div>
                )}

                {/* Voice Selector Modal */}
                {showVoiceModal && callsForm.callEnabled && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
                      {/* Modal Header */}
                      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                        <h3 className="text-lg font-semibold">Selecciona una Voz ({availableCallVoices.length} disponibles)</h3>
                        <button
                          onClick={() => setShowVoiceModal(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      {/* Modal Body - Voice List */}
                      <div className="p-4 overflow-y-auto max-h-[60vh]">
                        {availableCallVoices.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <p>Cargando voces disponibles...</p>
                          </div>
                        ) : (
                          <div className="grid gap-2">
                            {availableCallVoices
                              .filter(voice => {
                                if (!voiceSearchTerm) return true;
                                const searchLower = voiceSearchTerm.toLowerCase();
                                return (
                                  voice.voice_name?.toLowerCase().includes(searchLower) ||
                                  voice.language?.toLowerCase().includes(searchLower) ||
                                  voice.gender?.toLowerCase().includes(searchLower) ||
                                  voice.accent?.toLowerCase().includes(searchLower) ||
                                  voice.voice_id?.toLowerCase().includes(searchLower)
                                );
                              })
                              .map(voice => (
                                <button
                                  key={voice.voice_id}
                                  type="button"
                                  onClick={() => {
                                    setCallsForm({...callsForm, agentVoice: voice.voice_id});
                                    setShowVoiceModal(false);
                                    setVoiceSearchTerm('');
                                  }}
                                  className={`p-3 border rounded text-left hover:bg-blue-50 transition-colors ${
                                    callsForm.agentVoice === voice.voice_id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                                  }`}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-900">{voice.voice_name || voice.voice_id}</div>
                                      <div className="text-sm text-gray-600 mt-1 space-x-2">
                                        {voice.language && <span className="inline-block px-2 py-0.5 bg-gray-100 rounded">{voice.language}</span>}
                                        {voice.gender && <span className="inline-block px-2 py-0.5 bg-gray-100 rounded">{voice.gender}</span>}
                                        {voice.accent && <span className="inline-block px-2 py-0.5 bg-gray-100 rounded">{voice.accent}</span>}
                                      </div>
                                      {voice.preview_audio_url && (
                                        <div className="mt-2">
                                          <audio controls className="w-full max-w-xs h-8">
                                            <source src={voice.preview_audio_url} type="audio/mpeg" />
                                          </audio>
                                        </div>
                                      )}
                                    </div>
                                    {callsForm.agentVoice === voice.voice_id && (
                                      <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </div>
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="label">Idioma</label>
              <select
                value={callsForm.agentLanguage}
                onChange={(e) => setCallsForm({...callsForm, agentLanguage: e.target.value})}
                className="input-field"
                disabled={!callsForm.callEnabled}
              >
                <option value="es">Español</option>
                <option value="en">English</option>
                <option value="pt">Português</option>
              </select>
            </div>
          </div>
        </div>

        {/* Configuración de Teléfono (Híbrida) */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-semibold mb-4">Número de Teléfono (Twilio)</h3>

          <div className="space-y-3">
            {/* Opción 1: Usar número global */}
            <label className="flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="phoneOption"
                checked={!callsForm.useCustomPhone}
                onChange={() => setCallsForm({...callsForm, useCustomPhone: false})}
                className="mt-1 mr-3"
                disabled={!callsForm.callEnabled}
              />
              <div>
                <div className="font-medium">Usar número global del sistema</div>
                <div className="text-sm text-gray-600 mt-1">
                  Número configurado en el servidor:
                  <code className="ml-2 bg-gray-100 px-2 py-1 rounded">
                    {globalTwilioNumber || 'No configurado'}
                  </code>
                </div>
              </div>
            </label>

            {/* Opción 2: Usar número personalizado */}
            <label className="flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="phoneOption"
                checked={callsForm.useCustomPhone}
                onChange={() => setCallsForm({...callsForm, useCustomPhone: true})}
                className="mt-1 mr-3"
                disabled={!callsForm.callEnabled}
              />
              <div className="flex-1">
                <div className="font-medium">Usar número personalizado para este proyecto</div>
                <div className="text-sm text-gray-600 mb-2">
                  Selecciona un número específico de Twilio para este proyecto
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Script de Llamada */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-semibold mb-4">Script de la Llamada</h3>

          <div className="space-y-4">
            <div>
              <label className="label">Prompt del Sistema (Instrucciones para el agente)</label>
              <textarea
                value={callsForm.systemPrompt}
                onChange={(e) => setCallsForm({...callsForm, systemPrompt: e.target.value})}
                rows="8"
                className="input-field"
                placeholder="Eres un asistente virtual amable que llama a invitados para confirmar asistencia a un evento..."
                disabled={!callsForm.callEnabled}
              />
              <p className="text-sm text-gray-500 mt-1">
                Define cómo debe comportarse el agente durante la llamada
              </p>
            </div>

            <div>
              <label className="label">Primer Mensaje (Lo primero que dirá el agente)</label>
              <input
                type="text"
                value={callsForm.firstMessage}
                onChange={(e) => setCallsForm({...callsForm, firstMessage: e.target.value})}
                className="input-field"
                placeholder="Hola {name}, te llamo de {project}..."
                disabled={!callsForm.callEnabled}
              />
              <p className="text-sm text-gray-500 mt-1">
                Variables disponibles: <code>{'{name}'}</code>, <code>{'{project}'}</code>
              </p>
            </div>

            <div>
              <label className="label">Duración Máxima de Llamada (segundos)</label>
              <input
                type="number"
                value={callsForm.maxCallDuration}
                onChange={(e) => setCallsForm({...callsForm, maxCallDuration: e.target.value})}
                className="input-field"
                min="60"
                max="600"
                disabled={!callsForm.callEnabled}
              />
              <p className="text-sm text-gray-500 mt-1">
                Recomendado: 180-300 segundos (3-5 minutos)
              </p>
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="pt-4 space-y-4">
          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Guardando...' : 'Guardar Configuración'}
            </button>

            <button
              type="button"
              onClick={handleConfigureTwilioWebhook}
              disabled={loading}
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Configurar Webhook de Twilio
            </button>

            <button
              type="button"
              onClick={handleConfigureSIPAuth}
              disabled={loading}
              className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="CRÍTICO: Configura autenticación SIP para que funcionen las llamadas salientes"
            >
              Configurar Autenticación SIP
            </button>

            <button
              type="button"
              onClick={handleRunDiagnostic}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Ejecutar diagnóstico completo de la configuración"
            >
              Diagnóstico Completo
            </button>

            {callsForm.callEnabled && attendees.length > 0 && (
              <button
                type="button"
                onClick={handleStartCallCampaign}
                disabled={loadingCalls}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingCalls ? 'Iniciando...' : `Iniciar Campaña (${attendees.length} llamadas)`}
              </button>
            )}
          </div>
        </div>
      </form>

      {/* Historial de Llamadas */}
      {callHistory.length > 0 && (
        <div className="mt-8 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold mb-4">Historial de Llamadas</h3>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asistente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duración</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confirmó</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {callHistory.map((call) => (
                  <tr key={call.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {call.attendee_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {call.phone_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                        ${call.status === 'completed' ? 'bg-green-100 text-green-800' :
                          call.status === 'failed' ? 'bg-red-100 text-red-800' :
                          call.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'}`}>
                        {call.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {call.call_duration ? `${call.call_duration}s` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {call.attendee_confirmed ?
                        <span className="text-green-600">Sí</span> :
                        <span className="text-gray-400">-</span>
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(call.created_at).toLocaleString('es-ES')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallsTab;
